import type {GraphAtom} from "../graph/GraphAtom"
import type {GraphBond} from "../graph/GraphBond"
import {MolecularGraph} from "../graph/MolecularGraph"
import {Features} from "../feature";

// One-one correspondence between atom and acid name
export const acid_halide_features_names: Record<string, string> = {
    As: Features.astatide,
    Br: Features.bromide,
    Cl: Features.chloride,
    F: Features.flouride,
    I: Features.iodide,
    Ts: Features.tennesside
}

export const extractorUtil = {
    getFunctionalGroupRootsWithElement: (e: string, graph: MolecularGraph) => {
        // Functional groups always start with a single bonded connection to carbon.
        const candidates = graph.atoms.filter(a => a.symbol === e);
        return candidates.filter(c => extractorUtil.hasSingleBondToC(c, graph));
    },
    hasSingleBondToC: (a: GraphAtom, graph: MolecularGraph) => {
        return extractorUtil.getSingleBondsToC(a, graph).length > 0
    },
    getNonHBonds: (a: GraphAtom, graph: MolecularGraph) => {
        return graph.getBondsAttachedTo(a).filter(b => b.a1.symbol !== "H" && b.a2.symbol !== "H");
    },
    getNonHConnectedAtoms: (a: GraphAtom, graph: MolecularGraph) => {
        return extractorUtil.getNonHBonds(a, graph).map(b => b.getOtherAtom(a));
    },
    getFilteredNonHConnectedAtoms: (a: GraphAtom, graph: MolecularGraph, exclusionList: GraphAtom[]) => {
        return extractorUtil.getNonHConnectedAtoms(a, graph).filter(a => !exclusionList.includes(a));
    },
    getSingleBondsToC: (a: GraphAtom, graph: MolecularGraph) => {
        return graph.getBondsAttachedTo(a).filter(b => extractorUtil.isSingleBondToCFrom(b, a))
    },
    getSingleBondsToH: (a: GraphAtom, graph: MolecularGraph) => {
        return graph.getBondsAttachedTo(a).filter(b => extractorUtil.isSingleBondToHFrom(b, a))
    },
    getTotalHydrogenCount: (a: GraphAtom, graph: MolecularGraph): number => {
        const explicitHBonds = extractorUtil.getSingleBondsToH(a, graph);
        return explicitHBonds.length + a.implicitH;
    },
    getDoubleBondsToO: (a: GraphAtom, graph: MolecularGraph) => {
        return graph.getBondsAttachedTo(a).filter(b => b.getOtherAtom(a).symbol === "O" && b.order === 2)
    },
    getSingleBondsToO: (a: GraphAtom, graph: MolecularGraph) => {
        return graph.getBondsAttachedTo(a).filter(b => b.getOtherAtom(a).symbol === "O" && b.order === 1)
    },
    sortCFirst: (a: GraphAtom[]) => {
        // Joe and Sarah 07/09/25 "R" is in the sketcher vernacular so treat "R" as a "C"
        const isCarbonLike = (symbol: string) => symbol === "C" || symbol === "R";
        return a.sort((a: GraphAtom, b: GraphAtom) => {
            if (isCarbonLike(a.symbol) && !isCarbonLike(b.symbol)) return -1;
            if (!isCarbonLike(a.symbol) && isCarbonLike(b.symbol)) return 1;
            return 0;
        });
    },
    getAcidHalideNameFor: (e: string) => {
        return acid_halide_features_names[e] || Features.halide
    },
    isSingleBondToH: (b: GraphBond) => b.order === 1 &&
        ((b.a1.symbol === "H" && b.a2.symbol !== "H") ||
            (b.a1.symbol !== "H" && b.a2.symbol === "H"))
    ,
    isSingleBondToHFrom: (b: GraphBond, a: GraphAtom) => b.getOtherAtom(a).symbol === "H" && b.order === 1,
    isSingleBondToCFrom: (b: GraphBond, a: GraphAtom) => {
        // Joe and Sarah [07/09/25] R is now in the sketcher vernacular add "R" specifically as another "C" type.
        // Joe and Sarah [08/11/25] "Unknown" atoms are also considered R groups.
        const otherAtom = b.getOtherAtom(a);
        return b.order === 1 && (otherAtom.symbol === "C" || otherAtom.symbol === "R" || otherAtom.atomicNumber === 0);
    },
    findAllRingAtoms: (graph: MolecularGraph) => {
        return graph.getRings();
    },
    findRingNetwork: (r: GraphAtom[], graph: MolecularGraph) => {
        const network = new Set<GraphBond>();
        r.forEach(a => {
            const networkBonds = graph.getBondsAttachedTo(a).filter(b => r.includes(b.getOtherAtom(a)));
            networkBonds.forEach(b => network.add(b));
        })
        return Array.from(network);
    },
    findAtomChainNetworks: (graph: MolecularGraph) => { // H should not be part of chain
        // When finding carbon chains, rings are terminal so remove those from the candidate sets
        const ringAtoms = extractorUtil.findAllRingAtoms(graph).flat();
        const candidateAtoms = graph.atoms.filter(a => !ringAtoms.includes(a) && a.symbol !== "H");
        return candidateAtoms.reduce((acc, atom) => {
            const atomBonds = graph.getBondsAttachedTo(atom).filter(b => {
                const otherAtom = b.getOtherAtom(atom);
                return otherAtom.symbol !== "H" && !ringAtoms.includes(otherAtom)
            })
            acc.set(atom, atomBonds);
            return acc;
        }, new Map<GraphAtom, GraphBond[]>())
    },
    findCarbonChainNetworks: (graph: MolecularGraph) => {
        // When finding carbon chains, rings are terminal so remove those from the candidate sets
        const ringAtoms = extractorUtil.findAllRingAtoms(graph).flat();
        const allCarbons = graph.atoms.filter(a => a.symbol === "C");
        const candidateCarbons = allCarbons.filter(c => !ringAtoms.includes(c));
        return candidateCarbons.reduce((acc, c) => {
            const carbonBonds = graph.getBondsAttachedTo(c).filter(b => {
                const otherAtom = b.getOtherAtom(c);
                return otherAtom.symbol === "C" && !ringAtoms.includes(otherAtom)
            })
            acc.set(c, carbonBonds);
            return acc;
        }, new Map<GraphAtom, GraphBond[]>())
    },
    getHeavyAtoms: (atoms: GraphAtom[]) => atoms.filter(a => a.symbol !== "H"),
    getInternalBonds: (atoms: GraphAtom[], graph: MolecularGraph) => {
        return graph.bonds.filter(b => atoms.includes(b.a1) && atoms.includes(b.a2))
    },
    getExternalBonds: (internalAtoms: GraphAtom[], graph: MolecularGraph) => {
        return graph.bonds.filter(b =>
            (internalAtoms.includes(b.a1) ? 1 : 0) +
            (internalAtoms.includes(b.a2) ? 1 : 0) === 1)
    },
    areHeavyAtomsInRing: (feature: GraphAtom[], ring: GraphAtom[]) => {
        return extractorUtil.getHeavyAtoms(feature).every(a => ring.includes(a))
    }
}
